
import { ProductCard } from '@/components/ProductCard';
import { Product } from '@/types';
import { DbProduct } from '@/hooks/useProduct';

interface RelatedProductsProps {
  relatedProducts?: DbProduct[];
  isLoadingRelated: boolean;
  compact?: boolean;
}

export const RelatedProducts = ({ relatedProducts, isLoadingRelated, compact = false }: RelatedProductsProps) => {
  const displayProducts = compact && relatedProducts ? relatedProducts.slice(0, 5) : relatedProducts;
  const displayCount = compact ? 5 : 4;

  if (compact) {
    return (
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">You might also like</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {isLoadingRelated ? (
            Array.from({ length: displayCount }).map((_, i) => (
              <div key={i}>
                <div className="aspect-square w-full rounded-lg bg-muted animate-pulse mb-2" />
                <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
              </div>
            ))
          ) : (
            displayProducts?.map((p) => {
              const variant = p.defaultVariant;
              // Check variant images from product_variants nested data first
              const variantImages = (p as any).product_variants?.[0]?.variant_images;
              const variantImageUrl = variantImages?.[0]?.image_url;
              const displayImage = variant?.imageUrl || variantImageUrl || p.image_urls?.[0] || "/placeholder.svg";

              return (
                <a 
                  key={p.id} 
                  href={`/product/${p.id}`}
                  className="group cursor-pointer"
                >
                  <div className="aspect-square w-full rounded-lg overflow-hidden bg-muted mb-2">
                    <img 
                      src={displayImage} 
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-sm font-medium text-foreground text-center">{p.name}</p>
                </a>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-24">
      <h2 className="text-3xl font-bold text-center mb-8">You might also like</h2>
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
        {isLoadingRelated ? (
          Array.from({ length: displayCount }).map((_, i) => (
            <div key={i} className="flex flex-col space-y-3">
              <div className="h-[300px] w-full rounded-xl bg-muted animate-pulse" />
              <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
              <div className="h-4 w-1/4 rounded bg-muted animate-pulse" />
            </div>
          ))
        ) : (
          displayProducts?.map((p) => {
            const variant = p.defaultVariant;
            // Check variant images from product_variants nested data first
            const variantImages = (p as any).product_variants?.[0]?.variant_images;
            const variantImageUrl = variantImages?.[0]?.image_url;
            const displayImage = variantImageUrl || variant?.imageUrl || p.image_urls?.[0] || "/placeholder.svg";
            const displayPrice = variant?.price || p.price;
            const discountPrice = variant?.isDiscounted && variant?.discountPrice ? variant.discountPrice : null;
            const isDiscounted = variant?.isDiscounted || false;

            return (
              <ProductCard 
                key={p.id} 
                id={p.id}
                name={p.name}
                price={displayPrice}
                image={displayImage}
                discountPrice={discountPrice || undefined}
                isDiscounted={isDiscounted}
                hideColorSelector={true}
              />
            );
          })
        )}
      </div>
    </div>
  );
};
